import React, { useState, useEffect } from 'react';
import { Box, Alert, Chip } from '@mui/material';
import {
  ViiluDataGrid, PageHeader, PageContent, useRouter,
  RecordState, getRecordStateLabel, getRecordStateColor, getEnumValueOptions,
} from '@viilutalous/common';
import { categoriesAPI } from './expensesAPI';
import { useTranslation } from 'react-i18next';

export default function ExpenseCategories({ tenantId }) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchCategories = async () => {
    if (!tenantId) return;

    try {
      const response = await categoriesAPI.getAll(tenantId);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    navigate('/expense-categories/new');
  };

  const handleEdit = (category) => {
    navigate(`/expense-categories/${category.id}`);
  };

  const handleDelete = async (id) => {
    if (!tenantId) return;

    if (window.confirm(t('expense_categories.delete_confirm'))) {
      try {
        await categoriesAPI.delete(tenantId, id);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting expense category:', error);
      }
    }
  };

  const getContextMenuItems = (params) => {
    const result = [
      {
        name: t('common.new'),
        action: () => navigate('/expense-categories/new'),
        icon: '<i class="ag-icon ag-icon-plus"/>',
        shortcut: 'Ins',
      },
    ];

    if (!params.node) return result;

    const category = params.node.data;
    result.push('separator');
    result.push({
      name: t('common.edit'),
      action: () => handleEdit(category),
      icon: '<i class="ag-icon ag-icon-edit"/>',
      shortcut: 'Enter',
    });

    result.push('separator');
    result.push({
      name: t('common.delete'),
      action: () => handleDelete(category.id),
      icon: '<i class="ag-icon ag-icon-cancel"/>',
      cssClasses: ['ag-menu-option-text-danger'],
    });

    return result;
  };

  const StateCellRenderer = ({ value }) => (
    <Chip
      label={t(`common.state.${getRecordStateLabel(value)}`)}
      color={getRecordStateColor(value)}
      size="small"
    />
  );

  const columnDefs = [
    { field: 'code', headerName: t('expense_categories.code'), width: 120 },
    { field: 'name', headerName: t('expense_categories.name'), flex: 1, minWidth: 200 },
    { field: 'unit_price', headerName: t('expense_categories.unit_price'), width: 130 },
    { field: 'unit', headerName: t('expense_categories.unit'), width: 120 },
    {
      field: 'state',
      headerName: t('expense_categories.state'),
      width: 120,
      cellRenderer: StateCellRenderer,
      valueOptions: getEnumValueOptions(RecordState, getRecordStateLabel, t, 'common.state'),
    },
  ];

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('expense_categories.select_tenant')}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <ViiluDataGrid
          tenantId={tenantId}
          gridId="expense-categories-list"
          rowData={categories}
          columnDefs={columnDefs}
          loading={loading}
          onRowDoubleClicked={(event) => navigate(`/expense-categories/${event.data.id}`)}
          getContextMenuItems={getContextMenuItems}
          onNew={handleNew}
          onEdit={(row) => navigate(`/expense-categories/${row.id}`)}
          onRefresh={fetchCategories}
        />
      </PageContent>
    </>
  );
}
